import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { CheckCircle, Cancel, Refresh, Edit, PersonAdd } from '@mui/icons-material';
import api from '../../services/api';

function RadiologistManagement() {
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [capacityDialog, setCapacityDialog] = useState({ open: false, radiologist: null, newCapacity: 10 });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    specialty: '',
    facility: '',  // optional for in-house
  });

  const radiologySpecialties = [
    'Neuroradiology',
    'Chest Radiology',
    'Cardiac Imaging',
    'Abdominal Imaging',
    'Musculoskeletal Radiology',
    'Breast Imaging',
    'Pediatric Radiology',
    'Interventional Radiology',
    'Nuclear Medicine',
    'General Radiology',
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all users with role 'radiologist' and performance data
      const [radRes, facRes] = await Promise.all([
        api.get('/admin/radiologists/performance'),
        api.get('/facilities'),
      ]);

      setSpecialists(radRes.data || []);
      setFacilities(facRes.data?.facilities || facRes.data || []);
    } catch (error) {
      console.error('Error loading radiologist data:', error);
      setError('Failed to load radiologist data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async (id, currentStatus) => {
    try {
      setError(null);
      setSuccess(null);
      
      await api.patch(`/admin/radiologists/${id}/status`, { 
        isAvailable: !currentStatus 
      });
      
      // Refresh list
      await loadData();
      setSuccess(`Radiologist availability ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling availability:', error);
      setError(error.response?.data?.error || 'Failed to update availability. Please try again.');
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      specialty: '',
      facility: '',
    });
    setError(null);
    setSuccess(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSaving(false);
  };

  const handleSubmitAdd = async () => {
    setError(null);
    setSuccess(null);

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.specialty) {
      setError('Please fill in all required fields (name, email, and specialty).');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        specialty: formData.specialty,
        role: 'radiologist',
        radiologistType: 'dycovue',
        ...(formData.facility ? { facility: formData.facility } : {}),
      };

      const response = await api.post('/auth/register-user', payload);
      const message = response.data?.message || 'In-house radiologist registered successfully.';
      setSuccess(message);
      setAddDialogOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error registering radiologist:', error);
      setError(error.response?.data?.error || error.response?.data?.details || 'Failed to register specialist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCapacityDialog = (rad) => {
    setCapacityDialog({
      open: true,
      radiologist: rad,
      newCapacity: rad.maxCapacity || 10
    });
  };

  const handleCloseCapacityDialog = () => {
    setCapacityDialog({ open: false, radiologist: null, newCapacity: 10 });
  };

  const handleUpdateCapacity = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      const response = await api.patch(`/admin/radiologists/${capacityDialog.radiologist._id}/capacity`, {
        maxCapacity: capacityDialog.newCapacity
      });
      
      if (response.data.success) {
        setSuccess(`Capacity updated to ${capacityDialog.newCapacity} reports`);
        handleCloseCapacityDialog();
        await loadData();
      }
    } catch (error) {
      console.error('Error updating capacity:', error);
      setError(error.response?.data?.error || 'Failed to update capacity. Please try again.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Dycovue Specialist Pool
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleOpenAddDialog}
          >
            Add Specialist
          </Button>
          <Tooltip title="Refresh data">
            <span>
              <IconButton onClick={loadData} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Radiologist</TableCell>
                        <TableCell>Specialty</TableCell>
                        <TableCell>Workload</TableCell>
                        <TableCell>Capacity</TableCell>
                        <TableCell>Avg. TAT (Mins)</TableCell>
                        <TableCell>Total RVU</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specialists.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No radiologists found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        specialists.map((rad) => {
                          const activeLoad = rad.activeLoad || 0;
                          const maxCapacity = rad.maxCapacity || 10;
                          const workloadPercentage = (activeLoad / maxCapacity) * 100;
                          const isAtCapacity = activeLoad >= maxCapacity;
                          
                          return (
                            <TableRow key={rad._id}>
                              <TableCell>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                Dr. {rad.lastName}, {rad.firstName}
                                <Chip
                                  label="DycoVue"
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#1e40af', color: '#fff', fontWeight: 700 }}
                                />
                              </Box>
                            </TableCell>
                              <TableCell>{rad.specialty || 'N/A'}</TableCell>
                              <TableCell>
                                <Box>
                                  <Chip 
                                    label={`${activeLoad}/${maxCapacity}`} 
                                    color={
                                      isAtCapacity ? 'error' : 
                                      workloadPercentage > 80 ? 'warning' : 
                                      'success'
                                    } 
                                    size="small" 
                                    sx={{ mb: 0.5 }}
                                  />
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={Math.min(workloadPercentage, 100)} 
                                    color={
                                      isAtCapacity ? 'error' : 
                                      workloadPercentage > 80 ? 'warning' : 
                                      'success'
                                    }
                                    sx={{ height: 4, borderRadius: 2 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body2">
                                    {maxCapacity}
                                  </Typography>
                                  <Tooltip title="Edit capacity">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleOpenCapacityDialog(rad)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {rad.averageTat
                                  ? `${(typeof rad.averageTat === 'number' ? rad.averageTat : parseFloat(rad.averageTat) || 0).toFixed(1)}min`
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={`${rad.reportCount || 0} approved report(s)`}>
                                  <span>{(rad.totalRvu || 0).toFixed(1)}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {rad.isAvailable && !isAtCapacity ? (
                                    <>
                                      <CheckCircle color="success" fontSize="small" />
                                      <Typography variant="body2" color="success.main">
                                        Accepting
                                      </Typography>
                                    </>
                                  ) : (
                                    <>
                                      <Cancel color="error" fontSize="small" />
                                      <Typography variant="body2" color="error.main">
                                        {isAtCapacity ? 'At Capacity' : 'Unavailable'}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {isAtCapacity ? (
                                  <Tooltip title="Cannot disable when at capacity">
                                    <span>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={rad.isAvailable !== false}
                                            onChange={() => toggleAvailability(rad._id, rad.isAvailable)}
                                            color="primary"
                                            disabled={isAtCapacity}
                                          />
                                        }
                                        label={rad.isAvailable ? 'Disable' : 'Enable'}
                                        labelPlacement="end"
                                      />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={rad.isAvailable !== false}
                                        onChange={() => toggleAvailability(rad._id, rad.isAvailable)}
                                        color="primary"
                                        disabled={isAtCapacity}
                                      />
                                    }
                                    label={rad.isAvailable ? 'Disable' : 'Enable'}
                                    labelPlacement="end"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Radiologist Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '680px' },
            maxWidth: '700px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, fontSize: '1.05rem', fontWeight: 700 }}>
          Add DycoVue In-House Specialist
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5, py: 0.5 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 1.5, py: 0.5 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mt: 0.2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Specialty</InputLabel>
                <Select
                  label="Specialty"
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                >
                  {radiologySpecialties.map((spec) => (
                    <MenuItem key={spec} value={spec}>
                      {spec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Primary Facility (Optional)</InputLabel>
                <Select
                  label="Primary Facility (Optional)"
                  value={formData.facility}
                  onChange={(e) => setFormData({ ...formData, facility: e.target.value })}
                >
                  <MenuItem value=""><em>None — can be assigned later via Facilities page</em></MenuItem>
                  {facilities.map((fac) => (
                    <MenuItem key={fac._id} value={fac._id}>
                      {fac.facilityName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={handleCloseAddDialog} disabled={saving} size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleSubmitAdd} variant="contained" disabled={saving} size="small" sx={{ textTransform: 'none', px: 2.5 }}>
            {saving ? 'Saving...' : 'Create In-House Specialist'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capacity Edit Dialog */}
      <Dialog
        open={capacityDialog.open}
        onClose={handleCloseCapacityDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, fontSize: '1rem', fontWeight: 700 }}>
          Update Capacity - {capacityDialog.radiologist && 
            `Dr. ${capacityDialog.radiologist.lastName}, ${capacityDialog.radiologist.firstName}`
          }
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.82rem' }}>
              Current workload: <strong>{capacityDialog.radiologist?.activeLoad || 0}</strong> reports
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Capacity"
              value={capacityDialog.newCapacity}
              onChange={(e) => setCapacityDialog(prev => ({
                ...prev,
                newCapacity: parseInt(e.target.value) || 1
              }))}
              inputProps={{ min: 1, max: 1000 }}
              sx={{ mt: 1.5 }}
              helperText="Maximum number of concurrent reports this radiologist can handle"
            />
            {capacityDialog.radiologist && capacityDialog.newCapacity < capacityDialog.radiologist.activeLoad && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                Warning: New capacity ({capacityDialog.newCapacity}) is below current workload ({capacityDialog.radiologist.activeLoad}). 
                This update will be rejected.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={handleCloseCapacityDialog} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button 
            onClick={handleUpdateCapacity} 
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', px: 2.5 }}
            disabled={
              !capacityDialog.newCapacity || 
              capacityDialog.newCapacity < 1 ||
              (capacityDialog.radiologist && capacityDialog.newCapacity < capacityDialog.radiologist.activeLoad)
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RadiologistManagement;